import React from 'react';
import { Popconfirm, Row, Tooltip } from 'antd';
import { Link } from 'react-router-dom';
import { PiPencil } from 'react-icons/pi';
import { RiErrorWarningLine } from 'react-icons/ri';
import { Delete } from 'react-iconly';
import { Eye } from 'iconsax-react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import isEmpty from 'lodash/isEmpty';
import dayjs from 'dayjs';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { FiExternalLink } from 'react-icons/fi';
import { IoEarth } from 'react-icons/io5';
import IntlMessages from '../../../../../layout/components/lang/IntlMessages';
import urlPageCategoryDetails from '../../../../../urls/urlPageCategoryDetails';
import urlPageTemplateDetails from '../../../../../urls/urlPageTemplateDetails';
import { HasRights, PrivateComponent } from '../../../../../components/HasRights/HasRights';
import { Permissions } from '../../../../../const/permissions';
import { DATE_TIME_FORMAT, EMPTY_IMAGE_PLACEHOLDER, EMPTY_VALUE_PLACEHOLDER } from '../../../../../const/system';
import getFormattedDate from '../../../../../lib/getFormattedDate';
import getDictionaryIdFromDynamicData from '../../../../../lib/getDictionaryIdFromDynamicData';
import PublishedTag from '../../../../../components/PublishedTag';
import CollapsibleText from '../../../../../components/CollapsibleText';



export const CATEGORIES_SOURCE = 'categories';
export const CATEGORY_TEMPLATE_CONTENT_TYPE = 'category';

export const getCategoriesTableColumns = (
  handleImageCardClick,
  handlePreviewDictionary,
  handleEdit,
  handleDelete,
  tableSize,
) => [
  {
    title: <IntlMessages id="ui-general-id" />,
    dataIndex: 'id',
    align: 'center',
    width: tableSize.width * 0.06,
    sorter: true,
  },
  {
    title: <IntlMessages id="ui-general-app-id" />,
    dataIndex: 'client_id',
    align: 'center',
    width: tableSize.width * 0.06,
    sorter: true,
  },
  {
    title: <IntlMessages id="ui-general-published" />,
    dataIndex: 'is_published',
    align: 'center',
    width: tableSize.width * 0.1,
    sorter: true,
    render: (value, item) => <PublishedTag published={Boolean(value)} publishedDate={item.published_at} />,
  },
  {
    title: <IntlMessages id="ui-general-preview" />,
    dataIndex: 'preview',
    align: 'center',
    width: tableSize.width * 0.11,
    render: (value, item) => (
      <LazyLoadImage
        onClick={() => {
          if (!isEmpty(item?.preview)) {
            handleImageCardClick(item.preview);
          }
        }}
        style={!isEmpty(item?.preview) ? { cursor: 'pointer' } : {}}
        className='hp-cursor-pointer'
        effect='blur'
        key={item?.id}
        alt="category preview"
        placeholderSrc={EMPTY_IMAGE_PLACEHOLDER}
        src={value ? `${value}?time=${Date.now()}` : EMPTY_IMAGE_PLACEHOLDER}
      />
    ),
  },
  {
    title: <IntlMessages id="ui-general-title" />,
    dataIndex: 'title',
    sorter: true,
    align: 'center',
    width: tableSize.width * 0.12,
    render: (item, value) => {
      return (
        <div>
          <CollapsibleText text={item} maxLength={200} />
          &nbsp;
          {value?.is_published && dayjs(value.updated_at).isAfter(dayjs(value.published_at)) && (
            <Tooltip placement="right" title={<IntlMessages id="ui-general-unpublished-tooltip-title" />}>
              &nbsp;
              <ExclamationCircleOutlined className="hp-text-color-warning-1" />
            </Tooltip>
          )}
        </div>
      );
    },
  },
  {
    title: <IntlMessages id="ui-general-template" />,
    align: 'center',
    dataIndex: 'template',
    sorter: true,
    width: tableSize.width * 0.12,
    render: (template) => {
      return (HasRights([ Permissions.CONSTRUCTORS.TEMPLATES.FIND ]) &&
        !isEmpty(template?.data?.title) ? (
          <Link style={{ wordBreak: 'break-all' }} to={urlPageTemplateDetails({ templateId: template?.data?.id })} target="_blank">
            {template?.data?.title}
            <FiExternalLink size={14} className="hp-ml-4" />
          </Link>
        ) : <div>{template?.data?.title ?? EMPTY_VALUE_PLACEHOLDER}</div>
      );
    },
  },
  {
    title: <IntlMessages id="ui-general-updated-at" />,
    dataIndex: 'updated_at',
    width: tableSize.width * 0.12,
    align: 'center',
    sorter: true,
    render: (value) => getFormattedDate(value, DATE_TIME_FORMAT),
  },
  ...(HasRights([
    Permissions.CONTENT.CATEGORIES.VIEW,
    Permissions.CONTENT.CATEGORIES.EDIT,
    Permissions.CONTENT.CATEGORIES.DELETE,
    Permissions.TRANSLATIONS.DICTIONARIES.DETAILS.VIEW,
  ]) ?
    [ {
      title: <IntlMessages id="ui-general-action" />,
      key: 'action',
      align: 'center',
      width: tableSize.width * 0.13,
      render: (item) => {
        const dictionaryId = getDictionaryIdFromDynamicData(item?.dynamic_data?.data);

        return (
          <Row className="da-h-100" align="middle" justify="center">
            <PrivateComponent allowedPermissions={[ Permissions.CONTENT.CATEGORIES.VIEW ]}>
              <Tooltip placement="top" title={<IntlMessages id="ui-general-view" />}>
                <div className="hp-text-right">
                  <Link
                    to={urlPageCategoryDetails({ categoryId: item.id })}
                    target="_blank"
                  >
                    <Eye
                      size={20}
                      data-testid="view-icon"
                      className="hp-cursor-pointer hp-transition hp-hover-text-color-warning-1 hp-text-color-black-80 hp-m-4"
                    />
                  </Link>
                </div>
              </Tooltip>
            </PrivateComponent>

            <PrivateComponent allowedPermissions={[ Permissions.CONTENT.CATEGORIES.EDIT ]}>
              <Tooltip placement="top" title={<IntlMessages id="ui-general-edit" />}>
                <div className="hp-text-right">
                  <PiPencil
                    onClick={() => handleEdit(item.id)}
                    size={20}
                    data-testid="edit-icon"
                    className="hp-cursor-pointer hp-transition hp-hover-text-color-warning-1 hp-text-color-black-80 hp-m-4"
                  />
                </div>
              </Tooltip>
            </PrivateComponent>

            <PrivateComponent allowedPermissions={[ Permissions.CONTENT.CATEGORIES.DELETE ]}>
              {!item.is_published ? (
                <Popconfirm
                  title={
                    <>
                      <div><IntlMessages id="ui-general-delete-confirm-message" /></div>
                      <IntlMessages id="categories-delete-notification" />
                    </>
                  }
                  placement="topLeft"
                  onConfirm={() => handleDelete(item.id)}
                  okText={<IntlMessages id="ui-general-yes" />}
                  cancelText={<IntlMessages id="ui-general-no" />}
                  icon={<RiErrorWarningLine className="remix-icon hp-text-color-primary-1" />}
                  okButtonProps={{ danger: true }}
                >
                  <Tooltip placement="top" title={<IntlMessages id="ui-general-delete" />}>
                    <div className="hp-text-right">
                      <Delete
                        size={20}
                        data-testid="delete-icon"
                        className="hp-cursor-pointer hp-transition hp-hover-text-color-danger-1 hp-text-color-black-80 hp-m-4"
                      />
                    </div>
                  </Tooltip>
                </Popconfirm>
              ) : (
                <Tooltip placement="top" title={<IntlMessages id="features-delete-notification" />}>
                  <div className="hp-text-right">
                    <Delete
                      disabled
                      size={20}
                      data-testid="delete-icon"
                      className="hp-opacity-4 hp-cursor-pointer hp-transition hp-text-color-black-80 hp-m-4"
                    />
                  </div>
                </Tooltip>
              )}
            </PrivateComponent>
            <PrivateComponent allowedPermissions={[ Permissions.TRANSLATIONS.DICTIONARIES.DETAILS.VIEW ]}>
              {dictionaryId && (
                <Tooltip placement="top" title={<IntlMessages id="ui-general-dictionary" />}>
                  <div className="hp-text-right">
                    <IoEarth
                      size={20}
                      data-testid="dictionary-icon"
                      onClick={() => handlePreviewDictionary(dictionaryId)}
                      className="hp-cursor-pointer hp-transition hp-hover-text-color-primary-1 hp-text-color-black-80 hp-m-4"
                    />
                  </div>
                </Tooltip>
              )}
            </PrivateComponent>
          </Row>
        );
      },
    } ] : []
  ),
];
